// Return stock to the vendor against a PO's received lines — extracted
// verbatim from the old Purchasing tab (F-FE-N97); opened from the order's
// detail sheet.
import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import type { PurchaseOrderDetail } from '@/types'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useCurrency } from '@/contexts/CurrencyContext'
import { fmtQty } from '@/lib/quantity'
import { returnCreditAmount } from './purchasing/purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasing/purchasingStrings'

const RS = PURCHASING_STRINGS.returns

type ToastFn = (type: 'success' | 'error', message: string) => void
type RefundType = 'cash' | 'bank' | 'supplier_credit' | 'no_refund'

export function PoReturnDialog({
  poId,
  onClose,
  showToast,
}: {
  poId: string | null
  onClose: () => void
  showToast: ToastFn
}) {
  const qc = useQueryClient()
  const open = !!poId

  const [lineId, setLineId] = useState('')
  const [qty, setQty] = useState('')
  const [refundType, setRefundType] = useState<RefundType>('supplier_credit')
  const [refundAmount, setRefundAmount] = useState('')
  // Pilot fix (Batch 5): the credit is pre-filled as qty × the line's price,
  // so a vendor credit always reduces what we owe; typing overrides it.
  const [amountTouched, setAmountTouched] = useState(false)
  const { formatCurrency } = useCurrency()
  const [reason, setReason] = useState('')
  const [pin, setPin] = useState('')

  const { data: detailRes } = useQuery({
    queryKey: ['purchaseOrder', poId, 'return'],
    queryFn: () => apiClient.getPurchaseOrder(poId!),
    enabled: open,
  })
  const detail: PurchaseOrderDetail | undefined = detailRes?.data
  const returnableLines = useMemo(
    () => (detail?.lines ?? []).filter((ln) => (ln.quantity_returnable ?? ln.quantity_received) > 0.0001 || (ln.quantity_elsewhere ?? 0) > 0.0001),
    [detail],
  )
  const line = returnableLines.find((ln) => ln.id === lineId)
  const returnable = line ? (line.quantity_returnable ?? line.quantity_received) : 0
  const elsewhere = line?.quantity_elsewhere ?? 0
  const overStore = line != null && Number(qty) > returnable + 0.000001
  // What the goods cost when they came in (a delivery can be priced above
  // the order), else the order's price.
  const creditPrice = line?.received_unit_cost ?? line?.unit_cost
  const autoCredit = returnCreditAmount(Number(qty), creditPrice)
  const shownAmount = amountTouched || refundType === 'no_refund' ? refundAmount : autoCredit != null ? String(autoCredit) : refundAmount

  const reset = () => {
    setLineId('')
    setQty('')
    setRefundType('supplier_credit')
    setRefundAmount('')
    setAmountTouched(false)
    setReason('')
    setPin('')
  }

  const close = () => {
    if (returnPO.isPending) return
    reset()
    onClose()
  }

  const returnPO = useMutation({
    mutationFn: () => {
      if (!poId) throw new Error('No purchase order selected')
      return apiClient.returnPurchaseOrderLine(poId, {
        purchase_order_line_id: lineId,
        quantity: Number(qty),
        refund_amount: refundType === 'no_refund' ? 0 : Number(shownAmount || 0),
        refund_type: refundType,
        reason: reason.trim(),
        pin: pin.trim(),
      })
    },
    onSuccess: (res) => {
      showToast('success', res.message || 'Return recorded')
      qc.invalidateQueries({ queryKey: ['purchaseOrders'] })
      qc.invalidateQueries({ queryKey: ['purchaseOrder'] })
      qc.invalidateQueries({ queryKey: ['stockItems'] })
      qc.invalidateQueries({ queryKey: ['stockMovements'] })
      qc.invalidateQueries({ queryKey: ['stockSummary'] })
      qc.invalidateQueries({ queryKey: ['advancedStockReport'] })
      qc.invalidateQueries({ queryKey: ['inventoryActivity'] })
      qc.invalidateQueries({ queryKey: ['expiringBatches'] }) // F-FE-N118
      qc.invalidateQueries({ queryKey: ['stockItem'] })
      qc.invalidateQueries({ queryKey: ['stockAlerts'] })
      qc.invalidateQueries({ queryKey: ['vendorLedger'] })
      reset()
      onClose()
    },
    onError: (e: Error) => showToast('error', e.message || 'Failed to record the return'),
  })

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Return stock to vendor</DialogTitle>
          <DialogDescription>
            Record stock sent back. This creates a return movement and keeps the original delivery
            history intact.
          </DialogDescription>
        </DialogHeader>
        {!detail ? (
          <p className="text-sm text-muted-foreground py-8">Loading order…</p>
        ) : returnableLines.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/20 p-5 text-sm text-muted-foreground">
            No returnable stock remains for this order. It may already have been used, adjusted, or returned.
          </div>
        ) : (
          <div className="space-y-4 py-1">
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              <p className="font-semibold text-foreground">{detail.supplier_name}</p>
              <p className="mt-1 text-muted-foreground">
                Choose the received line you are returning. Expiring batches are deducted first.
              </p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="po-return-line">Returned item</Label>
                <select
                  id="po-return-line"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={lineId}
                  onChange={(e) => { setLineId(e.target.value); setQty(''); setAmountTouched(false) }}
                >
                  <option value="">Select received line…</option>
                  {returnableLines.map((ln) => (
                    <option key={ln.id} value={ln.id} disabled={(ln.quantity_returnable ?? ln.quantity_received) <= 0.0001}>
                      {ln.item_name} · {ln.quantity_elsewhere != null
                        ? RS.inStore(fmtQty(ln.quantity_returnable ?? 0), ln.unit)
                        : `returnable ${(ln.quantity_returnable ?? ln.quantity_received).toFixed(2)} ${ln.unit}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="po-return-qty">Quantity</Label>
                <Input
                  id="po-return-qty"
                  type="number"
                  step="0.01"
                  min="0"
                  className="tabular-nums"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                />
                {overStore && <p className="text-sm text-danger-fg">{RS.overStore(fmtQty(returnable), line?.unit ?? '')}</p>}
                {line && elsewhere > 0.0001 && (
                  <p className="text-sm text-muted-foreground">{RS.inKitchen(fmtQty(elsewhere), line.unit)}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="po-return-refund-type">Refund type</Label>
                <select
                  id="po-return-refund-type"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={refundType}
                  onChange={(e) => {
                    const next = e.target.value as RefundType
                    setRefundType(next)
                    if (next === 'no_refund') setRefundAmount('')
                  }}
                >
                  <option value="supplier_credit">Vendor credit</option>
                  <option value="cash">Cash refund</option>
                  <option value="bank">Bank refund</option>
                  <option value="no_refund">No refund</option>
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="po-return-refund">{refundType === 'supplier_credit' ? RS.credit : RS.refund}</Label>
                <Input
                  id="po-return-refund"
                  type="number"
                  step="0.01"
                  min="0"
                  className="tabular-nums"
                  value={refundType === 'no_refund' ? '' : shownAmount}
                  disabled={refundType === 'no_refund'}
                  onChange={(e) => { setAmountTouched(true); setRefundAmount(e.target.value) }}
                  placeholder={refundType === 'no_refund' ? 'Not applicable' : '0.00'}
                />
                {refundType !== 'no_refund' && !amountTouched && autoCredit != null && line && creditPrice != null && (
                  <p className="text-xs text-muted-foreground">{RS.creditHelp(fmtQty(Number(qty)), line.unit, formatCurrency(creditPrice))}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="po-return-pin">Admin / manager PIN</Label>
                <Input
                  id="po-return-pin"
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  className="tracking-[0.4em]"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="po-return-reason">Reason</Label>
                <Textarea
                  id="po-return-reason"
                  className="min-h-24"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Example: Expired before use; vendor agreed to credit"
                />
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={close} disabled={returnPO.isPending}>
            Cancel
          </Button>
          <Button
            disabled={
              !lineId ||
              !(Number(qty) > 0) ||
              overStore ||
              !reason.trim() ||
              pin.length !== 4 ||
              returnPO.isPending ||
              (refundType !== 'no_refund' && Number(shownAmount || 0) < 0)
            }
            onClick={() => returnPO.mutate()}
          >
            {returnPO.isPending ? 'Recording…' : 'Record return'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
